import type { ReactNode } from "react";

/**
 * The one page header: eyebrow -> h1.display -> lead, with an actions slot.
 *
 * There is exactly ONE `<h1>` per document and this is it. The shell renders no
 * title of its own — a workspace that prints "Earnings" in the top bar and
 * "Earnings" again 60px below is the strongest "unfinished" signal a product
 * can send, and when the two disagree ("Find jobs" over "Leaking kitchen tap")
 * it is worse than saying nothing.
 *
 * `eyebrow` is the NAV SECTION this page belongs to, which is what makes the
 * pair read as a hierarchy instead of two competing headings. It is dropped on
 * screens whose <h1> IS the section name ("Earnings", "Messages").
 *
 * `back` names its DESTINATION — "All services", "Your profile", "Booking
 * setup" — never "Back to …": the chevron already says "back", and the words
 * are better spent on where it goes.
 *
 * The two must never say the same thing. One level under a section root the
 * destination IS the section, and passing both printed "Booking setup" as a
 * chevroned link and again as an eyebrow on the very next line. Where they
 * would collide the back link wins — it is the half that is also an affordance
 * — and the eyebrow goes. The sites' chrome contract asserts this.
 */
export function PageHead({
  eyebrow,
  title,
  lead,
  actions,
  back,
  headingRef,
}: {
  eyebrow?: string;
  title: string;
  lead?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  /** Route changes move focus here, not to a bar that never changes. */
  headingRef?: React.Ref<HTMLHeadingElement>;
}) {
  return (
    <header className="page-head">
      <div className="page-head__text">
        {back}
        {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
        <h1 className="display" ref={headingRef} tabIndex={-1}>
          {title}
        </h1>
        {lead ? <p className="lead">{lead}</p> : null}
      </div>
      {actions ? <div className="page-head__actions">{actions}</div> : null}
    </header>
  );
}
